import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import type { ClientLoaderFunctionArgs } from '@remix-run/react';
import { useRouteLoaderData } from '@remix-run/react';

import { withLazyBlocks } from '~/components/DreamBuilder/lazyBlock';
import { useMetaViewContent } from '~/components/Pixels/useMetaViewContent';
import StaticPage from '~/components/StaticPage';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { Page } from '~/interfaces/page';
import type { PodcastEpisode } from '~/interfaces/podcast';
import type { Site } from '~/interfaces/site';
import PodcastEpisodePaywall from '~/pages/podcast/PodcastEpisodePaywall';
import type { RootLoaderData } from '~/root';
import { fetchPodcastEpisode } from '~/routes/p/_utils/fetchPodcastEpisode';
import getPage from '~/services/api/pages/getPage.server';
import getEpisode from '~/services/api/podcasts/getEpisode.server';
import { getRenderableFontsCss } from '~/services/fonts';
import type { GoogleFontEntryType } from '~/services/fonts/cache';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import getMetaDataLinks from '~/utils/getMetaDataLinks';
import { publicationFromMatches, siteFromMatches } from '~/utils/publicationFromLoaderData';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

export type PodcastShowEpisodePageLoaderData = {
  page: Page;
  renderableFonts: string[];
  renderableFontsCss: GoogleFontEntryType[];
  adobeFontUrls: string[];
  requestUrl: string;
  podcastEpisode: PodcastEpisode | null;
  showSlug: string;
  episodeSlug: string;
  draft?: string;
};

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const podcastEpisode = data?.podcastEpisode;
  const publication = publicationFromMatches(matches);
  const requestUrl = data?.requestUrl || '';
  const schemaUrl = getCanonicalUrl(requestUrl);

  const imageUrl = podcastEpisode?.thumbnail_url || '';

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(requestUrl),
    matches,
    returnEarlyCheck: !podcastEpisode,
    pageMetadata: data?.page?.viewable_page_version?.metadata as Record<string, unknown> | undefined,
    siteMetadata: siteFromMatches(matches)?.viewable_site_version?.metadata as Record<string, unknown> | undefined,
    overrides: {
      title: podcastEpisode?.title || '',
      description: podcastEpisode?.description || '',
      'og:url': requestUrl,
      'og:title': podcastEpisode?.title || '',
      'og:description': podcastEpisode?.description || '',
      'og:site_name': publication?.name || '',
      'og:image': imageUrl || publication?.thumbnail?.url || publication?.logo?.url || '',
      'og:image:alt': podcastEpisode?.title || publication?.description || '',
      'twitter:url': requestUrl,
      'twitter:title': podcastEpisode?.title || '',
      'twitter:description': podcastEpisode?.description || '',
      'twitter:image': imageUrl || publication?.thumbnail?.url || publication?.logo?.url || '',
    },
    links: getMetaDataLinks({ renderableFonts: data?.renderableFonts || [] }),
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        identifier: podcastEpisode?.id || '',
        url: schemaUrl,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': schemaUrl,
        },
        headline: podcastEpisode?.title || '',
        description: podcastEpisode?.description || '',
        datePublished: podcastEpisode?.publish_date || '',
        image: imageUrl
          ? {
              '@type': 'ImageObject',
              url: imageUrl,
            }
          : undefined,
        publisher: publication
          ? {
              '@type': 'Organization',
              name: publication?.name || '',
              url: publication?.url || '',
              identifier: publication?.id || '',
              logo: publication?.logo?.url
                ? {
                    '@type': 'ImageObject',
                    url: publication.logo.url,
                    contentUrl: publication.logo.url,
                    thumbnailUrl: publication.logo?.thumb?.url,
                  }
                : undefined,
              image: publication?.logo?.url
                ? {
                    '@type': 'ImageObject',
                    url: publication.logo.url,
                    contentUrl: publication.logo.url,
                    thumbnailUrl: publication.logo?.thumb?.url,
                  }
                : undefined,
            }
          : undefined,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': publication?.url || '',
              name: publication?.name || '',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': schemaUrl,
              name: podcastEpisode?.title || '',
            },
          },
        ],
      },
    ],
  });
};

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);

  if (!params.showSlug || !params.episodeSlug) {
    return Response.json(
      {
        error: 'Show slug and episode slug are required',
      },
      { status: 400 },
    );
  }

  const [site, page, podcastEpisode] = await Promise.all([
    context.getSite(request, session),
    getPage(request, session, {
      pathname: 'podcast/s/$showSlug/$episodeSlug',
    }),
    getEpisode(request, session, params.showSlug, params.episodeSlug, url.searchParams.get('draft')),
  ]);

  if (!podcastEpisode) {
    return Response.json(
      {
        error: 'Podcast episode not found',
      },
      { status: 404 },
    );
  }

  const fontsData = await getRenderableFontsCss(site as Site, page);
  const renderableFontsCss = fontsData?.fontsCss ?? [];
  const adobeFontUrls = fontsData?.adobeFontUrls ?? [];

  return Response.json({
    page,
    podcastEpisode,
    renderableFontsCss,
    adobeFontUrls,
    requestUrl: url.href,
    showSlug: params.showSlug,
    episodeSlug: params.episodeSlug,
    draft: url.searchParams.get('draft'),
  });
};

export const clientLoader = withLazyBlocks(async ({ serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<PodcastShowEpisodePageLoaderData>();
  if (!serverData?.page) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  const showSlug = serverData.showSlug;
  const episodeSlug = serverData.episodeSlug;

  if (!showSlug || !episodeSlug) {
    return serverData;
  }

  const podcastEpisode = await fetchPodcastEpisode({
    showSlug,
    episodeSlug,
    draft: serverData.draft,
  });

  return {
    ...serverData,
    podcastEpisode,
  };
});

clientLoader.hydrate = true;

const Index = () => {
  const data = useTypedLoaderData<PodcastShowEpisodePageLoaderData>();
  const { page, podcastEpisode } = data;
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  useMetaViewContent(
    podcastEpisode?.id,
    podcastEpisode?.id
      ? {
          content_type: 'podcast_episode',
          content_ids: [podcastEpisode.id],
        }
      : null,
  );

  return (
    <StaticPage site={site} page={page} forceShowFooter forceShowNavbar isSearchModalEnabled>
      {podcastEpisode?.is_premium ? (
        <PodcastEpisodePaywall
          primaryTheme={site.primary_theme}
          backgroundColor={site.publication.web_theme.background_color}
          key={podcastEpisode?.id}
        />
      ) : null}
    </StaticPage>
  );
};

export const shouldRevalidate = shouldRevalidateLoaderData;

export default Index;
